// Derives a video's PDAs from its UUID and the connected wallet, and reads the
// on-chain Video account.
import { useConnection } from '@solana/wallet-adapter-react'
import type { AccountInfo, PublicKey } from '@solana/web3.js'
import { PublicKey as PublicKeyImpl } from '@solana/web3.js'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useProgramReader } from '../react/index.js'
import { videoIdBytes } from './encoding.js'
import { publisherPda as derivePublisherPda, videoPda as deriveVideoPda } from './pda.js'
import { FORUM_IDL, FORUM_PROGRAM_ID, type VideoAccount } from './program.js'

export interface ForumVideoState {
  publisherPda: PublicKey | null
  videoPda: PublicKey | null
  video: VideoAccount | null
  loading: boolean
  /** True when the account genuinely does not exist — a video that has never
   * been anchored — as opposed to not-loaded-yet. This is what decides
   * whether the UI offers "anchor" or "correct", so it must not collapse
   * into a null. */
  missing: boolean
  error: Error | null
  refresh: () => void
}

interface ReadState {
  video: VideoAccount | null
  loading: boolean
  missing: boolean
  error: Error | null
}

/**
 * Reads need no wallet, but the video PDA is derived from the *publisher's*
 * PDA, which is derived from an authority key. So when no wallet is connected
 * there is nothing to derive from: this returns a null videoPda and
 * missing: false, never a fabricated address.
 */
export function useForumVideo(videoId: string, authority: string | null): ForumVideoState {
  const { connection } = useConnection()
  const reader = useProgramReader(FORUM_IDL, FORUM_PROGRAM_ID)
  const [generation, setGeneration] = useState(0)
  const refresh = useCallback(() => setGeneration((g) => g + 1), [])

  // Both derivations throw on malformed input — a wallet handing back a bad
  // address, or a video id that is not a UUID. Throwing during render would
  // white-screen the whole admin page over a chain widget, so they degrade to
  // null and the caller shows "not anchored" instead.
  const publisherPda = useMemo(() => {
    if (!authority) return null
    try {
      return derivePublisherPda(new PublicKeyImpl(authority))
    } catch {
      return null
    }
  }, [authority])

  const videoPda = useMemo(() => {
    if (!publisherPda) return null
    try {
      return deriveVideoPda(publisherPda, videoIdBytes(videoId))
    } catch {
      return null
    }
  }, [publisherPda, videoId])

  const [state, setState] = useState<ReadState>({
    video: null,
    loading: videoPda !== null,
    missing: false,
    error: null,
  })

  useEffect(() => {
    if (!videoPda) {
      setState({ video: null, loading: false, missing: false, error: null })
      return
    }

    let live = true
    setState((s) => ({ ...s, loading: true }))

    // Decoded through the Program's own coder, which converts the IDL's Rust
    // field names to the camelCase the generated types promise. A bare
    // BorshAccountsCoder built from the raw JSON does not, and every field
    // reads undefined while TypeScript stays happy.
    //
    // Named 'video' rather than decodeAny: decodeAny would happily decode a
    // Publisher or a SubtitleTrack sitting at this address and hand it back
    // cast as a VideoAccount. Naming the account checks the discriminator and
    // throws instead.
    const apply = (raw: AccountInfo<Buffer> | null) => {
      if (!live) return
      if (!raw) {
        setState({ video: null, loading: false, missing: true, error: null })
        return
      }
      try {
        const decoded = reader.coder.accounts.decode<VideoAccount>('video', raw.data)
        setState({ video: decoded, loading: false, missing: false, error: null })
      } catch (err) {
        setState({ video: null, loading: false, missing: false, error: err as Error })
      }
    }

    connection
      .getAccountInfo(videoPda)
      .then(apply)
      .catch((err: Error) => live && setState({ video: null, loading: false, missing: false, error: err }))

    const sub = connection.onAccountChange(videoPda, apply)
    return () => {
      live = false
      // Leaking subscriptions keeps the websocket busy and the component alive.
      void connection.removeAccountChangeListener(sub)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- reader is a fresh
    // object every render; keyed on the address string and an explicit
    // refresh trigger instead, same discipline as useAccount.
  }, [connection, videoPda?.toBase58(), generation])

  return {
    publisherPda,
    videoPda,
    video: state.video,
    loading: state.loading,
    missing: state.missing,
    error: state.error,
    refresh,
  }
}
